import { MenuItem } from '../../auth/menu-items';

/**
 * The projection of a filtered MenuItem onto what the Kendo Drawer renders (CHAT-857).
 *
 * The Drawer takes a FLAT array and rebuilds the hierarchy itself from `id` / `parentId`,
 * so a group parent and its children are siblings in this list. `children` is carried on
 * the parent as well, purely so the collapsed rail's hover flyout has something to render
 * without re-deriving it.
 *
 * This is a presentational projection only. Privilege / tier / state filtering has already
 * happened in MenuService by the time a MenuItem reaches here - nothing in this file may
 * drop or add a row.
 */
export interface SidebarDrawerItem {
  id: string;
  parentId?: string;
  text: string;
  translate?: string;
  /** simple-line-icons class. Top-level rows and group parents only - see toDrawerItems. */
  icon?: string;
  link?: string;
  /** Further links that select this row - see MenuItem.activeLinks and selectedItemId. */
  activeLinks?: string[];
  elink?: string;
  target?: string;
  alert?: string;
  label?: string;
  isGroup: boolean;
  /** Kendo reads this to put `k-selected` on the row. */
  selected?: boolean;
  /** Kendo renders this as a bare rule and skips the item template entirely. */
  separator?: boolean;
  children?: SidebarDrawerItem[];
}

/** Id of the rule under the leading SuperAdmin block. */
export const SUPERADMIN_SEPARATOR_ID = 'separator:superadmin';

/**
 * Stable identity for a row. Same key the old template's trackBy used, so two rows that
 * were indistinguishable there stay indistinguishable here.
 */
export function drawerItemId(item: MenuItem): string {
  return `${item.text}|${item.link ?? item.elink ?? ''}`;
}

/** How many rows at the head of the menu are SuperAdmin-gated. */
function leadingSuperAdminCount(items: MenuItem[]): number {
  let count = 0;
  while (count < items.length && items[count].privilege === 'SuperAdmin') {
    count += 1;
  }
  // All-SuperAdmin would put a rule at the very bottom with nothing under it.
  return count === items.length ? 0 : count;
}

function project(item: MenuItem, isGroup: boolean): SidebarDrawerItem {
  return {
    id: drawerItemId(item),
    text: item.text,
    translate: item.translate,
    icon: item.icon,
    link: item.link,
    activeLinks: item.activeLinks,
    elink: item.elink,
    target: item.target,
    alert: item.alert,
    label: item.label,
    isGroup,
  };
}

/**
 * Flattens the filtered menu into the Drawer's items array.
 *
 * Two shape rules, both UCP Drawer parity:
 *
 *   * children carry NO icon. The icon column belongs to the rail; a second column of
 *     icons under an expanded group reads as another level of navigation rather than as
 *     detail of the row above it.
 *   * there is no group-name row inside a group. The Angle template rendered the group's
 *     own name as a grey `.sidebar-subnav-header` above its children, so an expanded
 *     "Conversation Flow" showed that name twice. The parent row is the heading.
 *
 * Anything deeper than one level of submenu is dropped: the Drawer's flat id/parentId
 * model is two levels, and MenuService already pins the menu at that depth.
 */
export function toDrawerItems(menuItems: MenuItem[]): SidebarDrawerItem[] {
  const flat: SidebarDrawerItem[] = [];
  const source = menuItems ?? [];

  // CHAT-855 pins the SuperAdmin-only rows at the top "so the permission boundary is
  // self-evident". UCP draws that kind of boundary as a Drawer separator, so this puts a
  // rule under the leading SuperAdmin run. A partner admin has those rows filtered out
  // before this runs, so the run is empty and no dangling rule is emitted.
  const superAdminRun = leadingSuperAdminCount(source);

  source.forEach((item, index) => {
    if (superAdminRun > 0 && index === superAdminRun) {
      flat.push({
        id: SUPERADMIN_SEPARATOR_ID,
        text: '',
        isGroup: false,
        separator: true,
      });
    }

    if (!item.submenu || item.submenu.length === 0) {
      flat.push(project(item, false));
      return;
    }

    const parent = project(item, true);
    const children = item.submenu.map((child) => ({
      ...project(child, false),
      parentId: parent.id,
      icon: undefined,
    }));

    parent.children = children;
    flat.push(parent, ...children);
  });

  return flat;
}

/**
 * Prefix match on whole path segments, which is what `routerLinkActive` did for these
 * rows. Segment-aware matters here: `/dialogs2` must not light up `/dialogs`.
 *
 * `/` is treated as exact-only - a bare-root link would otherwise match every route.
 *
 * The url's query string is ignored, except that a query param written on the LINK must
 * also be on the url (CHAT-1155). No menu `link` carries one; `activeLinks` entries do.
 */
export function isRouteMatch(link: string, url: string): boolean {
  if (!link || !url) {
    return false;
  }

  const path = url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const target = link.split(/[?#]/)[0].replace(/\/+$/, '') || '/';

  const pathMatches = target === '/'
    ? path === '/'
    : path === target || path.startsWith(`${target}/`);

  return pathMatches && hasQueryOf(link, url);
}

/** True when every query param on `link` is on `url` with the same value. */
function hasQueryOf(link: string, url: string): boolean {
  const required = new URLSearchParams(queryOf(link));
  const actual = new URLSearchParams(queryOf(url));
  let ok = true;
  required.forEach((value, key) => {
    if (actual.get(key) !== value) {
      ok = false;
    }
  });
  return ok;
}

function queryOf(url: string): string {
  const withoutFragment = url.split('#')[0];
  const index = withoutFragment.indexOf('?');
  return index < 0 ? '' : withoutFragment.substring(index + 1);
}

/**
 * The id of the row that owns the current URL, longest link wins so a child route beats
 * the section it sits under.
 *
 * A row's `activeLinks` compete on the same terms as its `link`. That is how a page with
 * no row of its own picks one: a channel's screens (/channels/42, /channels/new...) sit
 * under no list's link, and their ?role= query selects Client or Service Channels
 * (CHAT-1155). Length is the path's, so a query never outranks a deeper path.
 */
export function selectedItemId(
  items: SidebarDrawerItem[],
  url: string
): string | null {
  let best: SidebarDrawerItem | null = null;
  let bestLength = -1;

  (items ?? []).forEach((item) => {
    [item.link, ...(item.activeLinks ?? [])].forEach((link) => {
      if (!link || !isRouteMatch(link, url)) {
        return;
      }
      const length = link.split(/[?#]/)[0].length;
      if (length > bestLength) {
        best = item;
        bestLength = length;
      }
    });
  });

  return best ? best.id : null;
}
